// src/navigation/index.js
// 72차: StoreAdminTabNavigator — 매장관리 하단 탭을 매장관리(홈·통계)/운영관리(예약·설정)
//       두 개로 분리하고 공지·소식 탭 제거 (StoreAdminScreen의 section prop 활용)
// 72차: StoreInfoScreen 신규 등록 — 마이탭 "매장 관리" > "매장 정보"에서 연결
// 72차: 문의사항 기능 — Inquiries(매장/앱 응대), MyInquiries(내 문의 목록),
//       WriteInquiry(문의 작성, 예약내역/테마상세에서도 진입) 신규 등록
// 72차: 디자인 시스템 개편 — 하단 탭 아이콘을 이모지 → Ionicons 벡터 아이콘으로 교체
// 74차: Terms/Privacy 라우트 신규 등록 — 이용약관/개인정보처리방침을 외부 WebView 대신
//       앱 안 화면으로 이동 (LegalScreen.js)
// 74차: authRedirectSuppressed 도입 — 로그인 성공 즉시 Main으로 자동 이동하던 AppStack의
//       effect가 회원가입 완료 축하 화면 등과 경쟁하지 않도록 보류 플래그로 게이팅
// 74차: SocialOnboarding 라우트 신규 — SNS 신규가입자가 닉네임/약관 동의를 마칠 때까지
//       머무는 화면 (LoginScreen.js의 handleSocialAuthResult가 신규 유저를 여기로 보냄)
// 74차: 마이 탭 뱃지가 myPageBadge(모집 신청 대기)만 반영하고 notifBadge(안 읽은 알림)는
//       실시간 구독만 해두고 안 쓰던 버그 수정 — 두 값 합산해서 표시
// 75차: 비회원용 "로그인" 탭(마이 탭 자리에 GuestMyPageScreen을 보여주던 방식) 제거 —
//       비회원은 하단 탭 4개(홈/예약/모집/크루)만, 로그인 진입은 AppHeader 우측 링크로
//       대체(AppHeader.js). 로그인 사용자에게는 마이 탭이 그대로 5번째 탭으로 유지됨.
//       GuestMyPageScreen은 더 이상 쓰이지 않아 삭제.
// 76차: AppNavigator의 loading 게이트를 맨 ActivityIndicator 대신 SplashLoadingScreen으로
//       교체(App.js와 동일 이슈 — 스플래시가 로고 없는 스피너 화면으로 바로 바뀌어 보임).
import React, { useEffect, useMemo } from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '../contexts/AuthContext';
import { logout } from '../services/authService';
import { listenMyRecruitsPendingCount, listenUnreadNotificationCount, listenUnreadCrewMentionCount } from '../services/recruitService';
import { listenWriteableReservationsCount } from '../services/reviewService';
import SuperAdminScreen from '../screens/admin/SuperAdminScreen';
import SuperAdminStoresScreen from '../screens/admin/SuperAdminStoresScreen';
import SuperAdminUserReportScreen from '../screens/admin/SuperAdminUserReportScreen';
import StoreAdminScreen from '../screens/admin/StoreAdminScreen';
import { StoreAdminBranchContext } from '../contexts/StoreAdminBranchContext';
import LoginScreen from '../screens/auth/LoginScreen';
import RegisterScreen from '../screens/auth/RegisterScreen';
import ForgotPasswordScreen from '../screens/auth/ForgotPasswordScreen';
import SocialOnboardingScreen from '../screens/auth/SocialOnboardingScreen';
import { TermsScreen, PrivacyScreen } from '../screens/auth/LegalScreen';
import ForcedPasswordChangeScreen from '../screens/auth/ForcedPasswordChangeScreen';
import SplashLoadingScreen from '../components/common/SplashLoadingScreen';
import HomeScreen from '../screens/home/HomeScreen';
import BookingScreen from '../screens/booking/BookingScreen';
import BookingDetailScreen from '../screens/booking/BookingDetailScreen';
import ThemeDetailScreen from '../screens/booking/ThemeDetailScreen';
// 80차: dev 브랜치 신규 — 지역별 인기 테마 랭킹
import RegionalRankingScreen from '../screens/booking/RegionalRankingScreen';
import EventsScreen from '../screens/events/EventsScreen';
import MyPageScreen from '../screens/mypage/MyPageScreen';
import ReservationHistoryScreen from '../screens/mypage/ReservationHistoryScreen';
import WriteReviewScreen from '../screens/mypage/WriteReviewScreen';
import MyReviewsScreen from '../screens/mypage/MyReviewsScreen';
import FavoritesScreen from '../screens/mypage/FavoritesScreen';
import EditProfileScreen from '../screens/mypage/EditProfileScreen';
import EscapeDashboardScreen from '../screens/mypage/EscapeDashboardScreen';
import PointHistoryScreen from '../screens/mypage/PointHistoryScreen';
import InviteFriendScreen from '../screens/mypage/InviteFriendScreen';
import StoreInfoScreen from '../screens/mypage/StoreInfoScreen';
import StoreAdminFaqScreen from '../screens/mypage/StoreAdminFaqScreen';
import ReplyToReviewScreen from '../screens/mypage/ReplyToReviewScreen';
import InquiriesScreen from '../screens/mypage/InquiriesScreen';
import MyInquiriesScreen from '../screens/mypage/MyInquiriesScreen';
import WriteInquiryScreen from '../screens/mypage/WriteInquiryScreen';
import RecruitScreen from '../screens/recruit/RecruitScreen';
import RecruitWriteScreen from '../screens/recruit/RecruitWriteScreen';
import RecruitDetailScreen from '../screens/recruit/RecruitDetailScreen';
import CrewScreen from '../screens/crew/CrewScreen';
import CreateCrewScreen from '../screens/crew/CreateCrewScreen';
import CrewManageScreen from '../screens/crew/CrewManageScreen';
import CrewNoticesScreen from '../screens/crew/CrewNoticesScreen';
import StoreDetailScreen from '../screens/booking/StoreDetailScreen';
import NotificationsScreen from '../screens/mypage/NotificationsScreen';
import { COLORS } from '../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import { showAlert } from '../components/common/AppAlert';

function RecruitWriteGuard(props) {
  const { isLoggedIn } = useAuth();
  React.useEffect(() => {
    if (!isLoggedIn) {
      props.navigation.goBack();
      showAlert('로그인 필요', '모집글 작성은 로그인 후 이용 가능합니다.', [
        { text: '확인' },
        { text: '로그인하기', onPress: () => props.navigation.navigate('Login') },
      ]);
    }
  }, [isLoggedIn]);
  if (!isLoggedIn) return null;
  return <RecruitWriteScreen {...props} />;
}

const Stack = createNativeStackNavigator();
const Tab   = createBottomTabNavigator();

const GOLD       = COLORS.gold;
const SURFACE    = COLORS.surface;
const BORDER     = COLORS.border;
const TEXT_MUTED = COLORS.textMuted;
const RED        = COLORS.red;


// 78차 72차: 웹(Platform.OS==='web')에서는 항상 안드로이드용 고정값(64/10)만 쓰고
// 있었음 — 모바일 브라우저(특히 홈 인디케이터가 있는 아이폰)에서 그 아래 안전영역을
// 하나도 안 챙겨서 하단 탭바 라벨이 화면 밖으로 잘려 보이던 원인. 웹은 실제 기기의
// 안전영역을 CSS env()로 직접 반영(react-native-web이 문자열 CSS 값을 그대로 통과시킴).
// 80차 후속: [버그 수정] Android 네이티브(웹 아님)도 안전영역을 10px 고정값으로만
// 가정하고 있었음 — 제스처 내비게이션은 보통 이 정도지만, 3버튼 내비게이션이나 일부
// 제조사(삼성/샤오미 등) 기기는 하단 시스템 바가 그보다 훨씬 커서(실측 48px+ 사례 있음)
// 실제 안전영역보다 탭바 여백이 부족해 시스템 내비게이션 바가 탭바를 가리는 문제가
// 실기기에서 재현됨(사용자 제보 스크린샷으로 발견) — useSafeAreaInsets()로 실제 하단
// 안전영역을 반영하도록 훅으로 전환(정적 객체였던 걸 각 탭 네비게이터 컴포넌트 안에서
// 호출하는 함수로 변경).
// 80차 후속: [버그 수정, 실기기 디버그 텍스트로 확정] 위 수정(insets.bottom 반영) 이후에도
// 3버튼 내비게이션 에뮬레이터에서 여전히 겹침이 재현됨 — 디버그 텍스트로 실측한 결과
// insets.bottom 자체는 0이 아니라 24(정상적으로 읽힘)였지만, 이 값 자체가 edge-to-edge
// Android의 3버튼 내비게이션 바 실제 높이를 충분히 못 담고 있었음(react-native-safe-area
// -context가 Expo SDK54 기준 이미 최신 버전이라 라이브러리 업그레이드로는 해결 안 됨 —
// edge-to-edge+3버튼 내비게이션 조합에서 이 값이 실제보다 작게 잡히는 알려진 유형의
// 문제로 추정). insets.bottom을 신뢰하되, 최소 바닥값을 10→32로 올려 이런 기기에서도
// 최소한의 여유를 확보(제스처 내비게이션처럼 insets.bottom이 이미 32 이상인 기기는
// 기존과 동일하게 실측값 그대로 사용).
// 80차 후속: [버그 수정, 실기기 InsetsDebugOverlay 실측으로 확정] 32→48로도 "아주 살짝"
// 겹침이 재현됨 — 실측 결과(insets.bottom=48, screen-window 높이차 79 ≈ insets.top(31)+
// insets.bottom(48))로 보면 insets.bottom 자체는 대체로 정확했고, 오차는 반올림/렌더링
// 경계 수준의 몇 px 차이로 추정됨. 정확한 하한을 또 추측하는 대신, 실측값 위에 작은
// 안전마진(+8)을 항상 더해 이런 수준의 오차를 흡수하도록 함.
// 80차 후속: [성능 개선, fork로 발견] 이 훅이 반환하는 객체를 useMemo 없이 매 렌더마다
// 새로 만들고 있었음 — 이 훅을 쓰는 세 탭 네비게이터 전부 안 읽은 알림/모집신청 등
// 배지 카운트를 실시간 구독(listenXxxCount)하고 있어서, insets.bottom이 전혀 안 바뀐
// 배지 카운트 변경만으로도 매번 tabBarStyle 객체가 새로 생성돼 React Navigation이
// 불필요하게 재적용하고 있었음(동작 오류는 아니고 순수 낭비) — insets.bottom이 실제로
// 바뀔 때만 재계산되도록 메모이제이션.
const useTabScreenOptions = () => {
  const insets = useSafeAreaInsets();
  return useMemo(() => {
    const androidBottomPad = Math.max(32, insets.bottom + 8);
    return {
      headerShown: false,
      tabBarStyle: {
        backgroundColor: SURFACE, borderTopColor: BORDER, borderTopWidth: 1,
        height: Platform.OS === 'ios' ? 82 : Platform.OS === 'web' ? 74 : 54 + androidBottomPad,
        paddingBottom: Platform.OS === 'ios' ? 22 : Platform.OS === 'web' ? 'calc(10px + env(safe-area-inset-bottom, 0px))' : androidBottomPad,
        paddingTop: 8,
      },
      tabBarActiveTintColor: GOLD,
      tabBarInactiveTintColor: TEXT_MUTED,
      tabBarLabelStyle: { fontSize: 10, fontWeight: '600', marginTop: 2 },
    };
  }, [insets.bottom]);
};

// 72차: 이모지 탭 아이콘 → 벡터 아이콘(Ionicons)으로 교체. name은 outline 접미사를 뺀
// 베이스 이름을 받아 활성 상태면 채워진 아이콘, 비활성이면 outline 아이콘을 보여준다.
const tabIcon = (name, focused) => (
  <Ionicons name={focused ? name : `${name}-outline`} size={22} color={focused ? COLORS.gold : COLORS.textMuted} />
);

// 78차 66차: "마이" 탭에 배지 자체가 없어서 새 앱 문의(new_inquiry) 등이 쌓여도 하단
// 탭에서 전혀 알 수 없었음 — GeneralTabNavigator와 동일하게 안 읽은 알림 수를 구독.
const SuperAdminTabNavigator = () => {
  const { user } = useAuth();
  const tabScreenOptions = useTabScreenOptions();
  const [notifBadge, setNotifBadge] = React.useState(0);
  React.useEffect(() => {
    if (!user?.uid) return;
    const unsub = listenUnreadNotificationCount(user.uid, setNotifBadge);
    return () => unsub();
  }, [user?.uid]);

  return (
    <Tab.Navigator screenOptions={tabScreenOptions}>
      <Tab.Screen name="SuperAdmin"      component={SuperAdminScreen}           options={{ tabBarLabel: '대시보드', tabBarIcon: ({ focused }) => tabIcon('stats-chart', focused) }} />
      <Tab.Screen name="SuperStores"     component={SuperAdminStoresScreen}     options={{ tabBarLabel: '매장관리', tabBarIcon: ({ focused }) => tabIcon('storefront', focused) }} />
      <Tab.Screen name="SuperUserReport" component={SuperAdminUserReportScreen} options={{ tabBarLabel: '유저·신고', tabBarIcon: ({ focused }) => tabIcon('shield-checkmark', focused) }} />
      <Tab.Screen name="Events"          component={EventsScreen}               options={{ tabBarLabel: '공지·소식', tabBarIcon: ({ focused }) => tabIcon('megaphone', focused) }} />
      <Tab.Screen name="MyPage"          component={MyPageScreen}               options={{
        tabBarLabel: '마이',
        tabBarIcon: ({ focused }) => tabIcon('person', focused),
        tabBarBadge: notifBadge > 0 ? (notifBadge > 99 ? '99+' : notifBadge) : undefined,
        tabBarBadgeStyle: { backgroundColor: RED, fontSize: 9, minWidth: 16, height: 16, lineHeight: 16 },
      }} />
    </Tab.Navigator>
  );
};

const StoreAdminTabNavigator = () => {
  const { user, profile } = useAuth();
  const tabScreenOptions = useTabScreenOptions();
  // 78차 68차: storeAdminTier가 'staff'(직원용 제한 계정)면 매출·통계·정산이 보이는
  // "매장관리" 탭 자체를 렌더하지 않음 — 필드가 없는 기존 계정은 owner로 간주(하위호환).
  const isStaffTier = profile?.storeAdminTier === 'staff';
  const [notifBadge, setNotifBadge] = React.useState(0);
  React.useEffect(() => {
    if (!user?.uid) return;
    // 52차: 30초 폴링 → 실시간 리스너 (인수인계 #5)
    // 72차: adminOnly — 매장관리자 배지는 운영 알림(new_reservation/new_review)만 세서
    //       NotificationsScreen에 실제로 뜨는 목록과 숫자가 항상 일치하도록 함
    const unsub = listenUnreadNotificationCount(user.uid, setNotifBadge, { adminOnly: true });
    return () => unsub();
  }, [user?.uid]);

  // 72차: 매장관리 화면 하나에 홈/통계/예약/설정 4개 내부탭이 다 몰려 있어 복잡하다는
  // 피드백으로 하단 탭을 매장관리(홈·통계)/운영관리(예약·설정) 두 개로 분리.
  // 공지·소식 탭도 제거 — 그 안의 공지사항·PICK 콘텐츠는 고객 대상이라 매장 운영자가
  // 평소에 볼 이유가 적고, PICK 테마 등록/리뷰 답글 등 실제 운영 업무는 이미
  // "운영관리" 탭의 설정 안에서 처리 가능함.
  // 72차: 두 탭이 서로 다른 StoreAdminScreen 인스턴스가 되면서 지점(브랜치) 선택이
  // 공유되지 않던 문제 — StoreAdminBranchContext로 끌어올려 이 네비게이터가 소유하고,
  // 두 화면이 같은 selectedBranchKey를 읽고 쓰도록 한다.
  const [selectedBranchKey, setSelectedBranchKey] = React.useState(null);

  return (
    <StoreAdminBranchContext.Provider value={{ selectedBranchKey, setSelectedBranchKey }}>
      <Tab.Navigator screenOptions={tabScreenOptions}>
        {!isStaffTier && (
          <Tab.Screen name="StoreAdminDashboard" options={{ tabBarLabel: '매장관리', tabBarIcon: ({ focused }) => tabIcon('storefront', focused) }}>
            {() => <StoreAdminScreen section="dashboard" />}
          </Tab.Screen>
        )}
        <Tab.Screen name="StoreAdminOps" options={{ tabBarLabel: '운영관리', tabBarIcon: ({ focused }) => tabIcon('construct', focused) }}>
          {/* 78차: 대시보드 홈의 "고객 리뷰 답변" 바로가기가 route.params.initialSettingsView로 딥링크 */}
          {({ route }) => <StoreAdminScreen section="ops" initialSettingsView={route.params?.initialSettingsView} />}
        </Tab.Screen>
        <Tab.Screen name="Booking"    component={BookingScreen}    options={{ tabBarLabel: '테마조회', tabBarIcon: ({ focused }) => tabIcon('search', focused) }} />
        <Tab.Screen name="MyPage"     component={MyPageScreen}     options={{
          tabBarLabel: '마이',
          tabBarIcon: ({ focused }) => tabIcon('person', focused),
          tabBarBadge: notifBadge > 0 ? (notifBadge > 99 ? '99+' : notifBadge) : undefined,
          tabBarBadgeStyle: { backgroundColor: RED, fontSize: 9, minWidth: 16, height: 16, lineHeight: 16 },
        }} />
      </Tab.Navigator>
    </StoreAdminBranchContext.Provider>
  );
};

const GeneralTabNavigator = ({ isLoggedIn }) => {
  const { user } = useAuth();
  const tabScreenOptions = useTabScreenOptions();
  const [myPageBadge,   setMyPageBadge]   = React.useState(0);
  const [notifBadge,    setNotifBadge]    = React.useState(0);
  // 78차 68차: 마이탭 안에서는 "내 리뷰"(작성 가능 리뷰 수)도 배지로 보이는데, 이 탭
  // 총합(myPageBadge+notifBadge)엔 안 들어가 있어 "마이탭 안엔 3개인데 하단탭은 2개"로
  // 어긋나 보이던 것 — MyPageScreen.js와 동일한 소스(getWriteableReservations)로 합산.
  const [reviewBadge,   setReviewBadge]   = React.useState(0);
  // 80차 계속: 크루 탭 전용 — crew_mention 미읽음 개수(전체 뱃지와 별개로 크루 탭 아이콘에도 표시)
  const [crewMentionBadge, setCrewMentionBadge] = React.useState(0);

  React.useEffect(() => {
    if (!isLoggedIn || !user?.uid) { setMyPageBadge(0); setNotifBadge(0); setReviewBadge(0); setCrewMentionBadge(0); return; }
    // 52차: 30초 폴링 → 실시간 리스너 2개 (인수인계 #5)
    const unsubPending = listenMyRecruitsPendingCount(user.uid, setMyPageBadge);
    const unsubNotif   = listenUnreadNotificationCount(user.uid, setNotifBadge);
    // 78차 69차: 마운트 시 한 번만 fetch하던 것 → 실시간 구독으로 교체(리뷰 작성 즉시 반영)
    const unsubReview  = listenWriteableReservationsCount(user.uid, setReviewBadge);
    const unsubCrew    = listenUnreadCrewMentionCount(user.uid, setCrewMentionBadge);
    return () => { unsubPending(); unsubNotif(); unsubReview(); unsubCrew(); };
  }, [isLoggedIn, user?.uid]);

  return (
    <Tab.Navigator screenOptions={tabScreenOptions}>
      <Tab.Screen name="Home"    component={HomeScreen}
        options={{ tabBarIcon: ({ focused }) => tabIcon('home', focused), tabBarLabel: '홈' }} />
      <Tab.Screen name="Booking" component={BookingScreen}
        options={{ tabBarIcon: ({ focused }) => tabIcon('search', focused), tabBarLabel: '예약' }} />
      <Tab.Screen name="Recruit" component={RecruitScreen}
        options={{ tabBarIcon: ({ focused }) => tabIcon('people', focused), tabBarLabel: '모집' }} />
      <Tab.Screen name="Crew"    component={CrewScreen}
        options={{
          tabBarIcon: ({ focused }) => tabIcon('people-circle', focused), tabBarLabel: '크루',
          tabBarBadge: crewMentionBadge > 0 ? (crewMentionBadge > 99 ? '99+' : crewMentionBadge) : undefined,
          tabBarBadgeStyle: { backgroundColor: RED, fontSize: 9, minWidth: 16, height: 16, lineHeight: 16 },
        }} />
      {/* 75차: 비회원용 "로그인" 탭 제거 — 로그인 진입은 AppHeader 우측 링크로 대체
          (하단 탭은 4개로 축소, 마이페이지는 로그인 사용자에게만 계속 하단 탭으로 유지) */}
      {isLoggedIn && (
        <Tab.Screen name="MyPage" component={MyPageScreen}
          options={{
            tabBarIcon:  ({ focused }) => tabIcon('person', focused),
            tabBarLabel: '마이',
            // 74차: notifBadge(안 읽은 알림 수)를 실시간 구독만 해두고 표시엔 안 쓰던 버그 —
            // myPageBadge(모집 신청 대기)와 합산해서 마이 탭 뱃지에 반영
            // 78차 68차: reviewBadge(내 리뷰 작성 가능 수)도 합산 — 마이탭 안 배지 합계와 일치시킴
            tabBarBadge: (myPageBadge + notifBadge + reviewBadge) > 0
              ? (myPageBadge + notifBadge + reviewBadge > 99 ? '99+' : myPageBadge + notifBadge + reviewBadge)
              : undefined,
            tabBarBadgeStyle: { backgroundColor: RED, fontSize: 9, minWidth: 16, height: 16, lineHeight: 16 },
          }} />
      )}
    </Tab.Navigator>
  );
};

function MainTabNavigator() {
  const { isLoggedIn, adminRole, profile } = useAuth();
  const tabKey = `tab-${isLoggedIn ? (adminRole ?? 'user') : 'guest'}`;

  // 80차 후속: [버그 수정, 중간 심각도 — fork로 발견] 카카오/네이버 탈퇴 유령계정 부활
  // 도중(ensureSocialUserDoc의 revival write가 끝나기 전) 앱이 강제종료되면, 다음 실행 시
  // Firebase Auth 세션은 이미 유효하고 users 문서도 존재(isDeleted:true인 채로)해서
  // authRedirectSuppressed 같은 인메모리 가드 없이 그대로 로그인 완료 처리될 뻔했음 —
  // 닉네임 "탈퇴한 회원", 빈 이메일/사진인 깨진 프로필로 앱을 쓰게 되는 상태. isDeleted:true
  // 감지 시 강제 로그아웃(로그인 화면으로 돌아가 SNS 재로그인하면 정상적으로 온보딩부터
  // 다시 시작해 부활이 완료됨).
  useEffect(() => {
    if (isLoggedIn && profile?.isDeleted === true) logout().catch(() => {});
  }, [isLoggedIn, profile?.isDeleted]);
  if (isLoggedIn && profile?.isDeleted === true) return <SplashLoadingScreen />;

  // 76차: createStoreAdminAccountCF로 발급된 매장관리자는 mustChangePassword:true 상태로
  // 시작 — 임시 비밀번호를 계속 쓰지 않도록 다른 화면 진입 전에 변경을 강제한다.
  if (isLoggedIn && adminRole === 'store' && profile?.mustChangePassword === true)
    return <ForcedPasswordChangeScreen key={tabKey} />;
  if (isLoggedIn && adminRole === 'super') return <SuperAdminTabNavigator key={tabKey} />;
  if (isLoggedIn && adminRole === 'store') return <StoreAdminTabNavigator key={tabKey} />;
  return <GeneralTabNavigator key={tabKey} isLoggedIn={isLoggedIn} />;
}

function AppStack() {
  // 78차 56차: 로그인 완료 후 Login/Register/SocialOnboarding 모달을 자동으로 닫는
  // 로직이 예전엔 여기(부모 컴포넌트가 외부 navRef로 goBack/reset/navigate 호출)에
  // 중앙집중돼 있었는데, 실기기 반복 검증 결과 이 방식은 JS 라우트 상태만 바꿀 뿐
  // 네이티브 화면 전환을 전혀 못 걷어내는 것으로 최종 확인됨(하드웨어 뒤로가기로만
  // 걷힘) — 화면 각자가 자기 자신의 navigation prop으로 직접 닫는 방식(Login/Register/
  // SocialOnboarding 각 파일 참고)으로 완전히 이관, 여기 있던 중앙집중 effect는 삭제.

  return (
    <Stack.Navigator screenOptions={{ headerShown: false }}>
      <Stack.Screen name="Main" component={MainTabNavigator} />
      {/* 78차 56차: presentation:'fullScreenModal' 제거 — 실기기 라이브 테스트로 이
          fullScreenModal 프레젠테이션이 goBack()/reset()/navigate() 같은 "프로그래밍적"
          네비게이션 호출로는 네이티브 dismiss가 전혀 안 되고, 오직 실제 하드웨어 뒤로가기
          제스처로만 걷힌다는 것을 확인함(react-native-screens 특유의 동작으로 추정) —
          로그인/회원가입 성공 후 AppStack이 자동으로 화면을 닫으려 해도 계속 실패하던
          근본 원인. 기본 card 프레젠테이션(나머지 화면들이 이미 문제없이 쓰고 있음)으로
          교체 — 시각적으로는 슬라이드 방향만 달라지고 기능은 동일. */}
      <Stack.Screen name="Login"          component={LoginScreen}          options={{ headerShown: false, gestureEnabled: true }} />
      <Stack.Screen name="Register"       component={RegisterScreen}       options={{ headerShown: false, gestureEnabled: true }} />
      <Stack.Screen name="ForgotPassword" component={ForgotPasswordScreen} options={{ headerShown: false, gestureEnabled: true }} />
      <Stack.Screen name="Terms"   component={TermsScreen}   options={{ headerShown: false, gestureEnabled: true }} />
      <Stack.Screen name="Privacy" component={PrivacyScreen} options={{ headerShown: false, gestureEnabled: true }} />
      <Stack.Screen name="SocialOnboarding" component={SocialOnboardingScreen} options={{ headerShown: false, gestureEnabled: true }} />

      <Stack.Screen name="ThemeDetail"    component={ThemeDetailScreen}    options={{ headerShown: false }} />
      <Stack.Screen name="StoreDetail"    component={StoreDetailScreen}    options={{ headerShown: false }} />
      <Stack.Screen name="BookingDetail"  component={BookingDetailScreen}  options={{ headerShown: false }} />
      <Stack.Screen name="RegionalRanking" component={RegionalRankingScreen} options={{ headerShown: false }} />

      <Stack.Screen name="ReservationHistory" component={ReservationHistoryScreen} />
      <Stack.Screen name="WriteReview"        component={WriteReviewScreen} options={{ headerShown: false }} />
      <Stack.Screen name="MyReviews"          component={MyReviewsScreen} />
      <Stack.Screen name="Favorites"          component={FavoritesScreen} />
      <Stack.Screen name="EditProfile"        component={EditProfileScreen} />
      <Stack.Screen name="EscapeDashboard"    component={EscapeDashboardScreen} />
      <Stack.Screen name="PointHistory" component={PointHistoryScreen} options={{ headerShown: false }} />
      <Stack.Screen name="InviteFriend" component={InviteFriendScreen} options={{ headerShown: false }} />
      <Stack.Screen name="StoreInfo"    component={StoreInfoScreen}    options={{ headerShown: false }} />
      <Stack.Screen name="StoreAdminFaq" component={StoreAdminFaqScreen} options={{ headerShown: false }} />
      <Stack.Screen name="ReplyToReview" component={ReplyToReviewScreen} options={{ headerShown: false }} />
      <Stack.Screen name="Inquiries"     component={InquiriesScreen}     options={{ headerShown: false }} />
      <Stack.Screen name="MyInquiries"   component={MyInquiriesScreen}   options={{ headerShown: false }} />
      <Stack.Screen name="WriteInquiry"  component={WriteInquiryScreen}  options={{ headerShown: false }} />
      <Stack.Screen name="Notifications"      component={NotificationsScreen} />
      <Stack.Screen name="RecruitWrite"       component={RecruitWriteGuard} />
      <Stack.Screen name="RecruitDetail"      component={RecruitDetailScreen} />
      <Stack.Screen name="WaitingList"        component={RecruitScreen} />
      {/* 74차: 공지·소식 탭을 크루 탭으로 교체하면서, 공지사항/앱소식은 마이 탭에서 진입하는
          일반 화면으로 이동(PICK은 예약 탭 기본 화면으로 옮겨서 EventsScreen.js에서 제거) */}
      <Stack.Screen name="Events"       component={EventsScreen} />
      {/* 78차 56차: 위 Login 등과 동일한 이유로 fullScreenModal 제거 */}
      <Stack.Screen name="CreateCrew"   component={CreateCrewScreen} options={{ headerShown: false, gestureEnabled: true }} />
      <Stack.Screen name="CrewManage"   component={CrewManageScreen} options={{ headerShown: false }} />
      <Stack.Screen name="CrewNotices" component={CrewNoticesScreen} options={{ headerShown: false }} />
    </Stack.Navigator>
  );
}

export default function AppNavigator() {
  const { loading } = useAuth();

  // 76차: 맨 스피너 대신 SplashLoadingScreen — 네이티브 스플래시와 시각적으로 이어지도록.
  // App.js가 이미 동일한 loading 값으로 이 컴포넌트 마운트 자체를 막고 있어 실질적으로는
  // 도달하지 않지만, 방어적으로 동일하게 처리.
  if (loading) return <SplashLoadingScreen />;

  return <AppStack />;
}